'use client';

import SvgCheck from '@common/icon/SvgCheck';
import SvgChevrondown from '@common/icon/SvgChevrondown';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { UnauthQueryOptions } from '@/api';

import type { FestivalDetailResponse } from '../../_utils/festivalFetchers';

const COLLAPSED_COUNT = 4;

const FILL_CLASSNAMES = [
  'bg-M-100',
  'bg-M-100/80',
  'bg-M-100/40',
  'bg-M-100/20',
];

interface FestivalGoodPointStatsProps {
  tourId: string;
  initialData: FestivalDetailResponse;
}

export const FestivalGoodPointStats = ({
  tourId,
  initialData,
}: FestivalGoodPointStatsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const collapsibleId = useId();

  const { data } = useQuery({
    ...UnauthQueryOptions['get:/wello/v1/allows/tour/festival/{tourId}']({
      path: { tourId },
    }),
    initialData,
  });

  const stats = data?.context?.goodPointStats ?? [];
  const participantCount = data?.context?.reviewCount;

  if (stats.length === 0) return null;

  const totalCount = stats.reduce((sum, stat) => sum + (stat.count ?? 0), 0);
  const maxCount = Math.max(...stats.map((stat) => stat.count ?? 0));
  const isExpandable = stats.length > COLLAPSED_COUNT;

  const visibleStats = isExpanded ? stats : stats.slice(0, COLLAPSED_COUNT);

  return (
    <div className="mt-32 flex w-full flex-col">
      <div className="flex flex-col gap-12">
        <div className="flex items-center justify-between gap-8">
          <h2 className="m-0 font-700 text-Gray-900 typo-body3">
            이런 점이 좋았어요
          </h2>
          <div className="flex shrink-0 items-center gap-4">
            <p className="m-0 flex items-center gap-2 font-700 text-M-400 typo-body3">
              <SvgCheck className="size-16" />
              {totalCount}회
            </p>
            {participantCount !== undefined ? (
              <p className="m-0 font-500 text-Gray-550 typo-body3">
                {participantCount}명 참여
              </p>
            ) : null}
          </div>
        </div>
        <ul
          id={collapsibleId}
          className="m-0 flex list-none flex-col gap-4 p-0"
        >
          {visibleStats.map((stat, index) => {
            const count = stat.count ?? 0;
            const isFaded =
              isExpandable && !isExpanded && index === COLLAPSED_COUNT - 1;

            return (
              <li
                key={stat.name ?? stat.desc}
                className="relative overflow-hidden rounded-xsmall bg-Gray-50"
              >
                <div
                  className={cn(
                    'absolute inset-y-0 left-0 rounded-xsmall',
                    FILL_CLASSNAMES[
                      Math.min(index, FILL_CLASSNAMES.length - 1)
                    ],
                  )}
                  style={{
                    width: `${maxCount > 0 ? (count / maxCount) * 100 : 0}%`,
                  }}
                />
                <div className="relative flex min-h-44 items-center justify-between gap-12 px-16 py-12">
                  <span className="min-w-0 break-keep font-600 text-Gray-900 typo-body4">
                    {stat.desc}
                  </span>
                  <span className="shrink-0 font-600 text-Gray-900 typo-body4">
                    {count}
                  </span>
                </div>
                {isFaded ? (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-b from-Gray-white/40 to-Gray-white"
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
      {isExpandable ? (
        <div className="relative flex h-32 items-center justify-center">
          <hr className="absolute inset-x-0 m-0 border-0 border-t border-Gray-200" />
          <button
            aria-expanded={isExpanded}
            aria-controls={collapsibleId}
            aria-label={
              isExpanded ? '좋았어요 통계 접기' : '좋았어요 통계 전체 보기'
            }
            onClick={() =>
              setIsExpanded((previousExpanded) => !previousExpanded)
            }
            className="relative flex size-32 items-center justify-center rounded-pill bg-Gray-200 transition-colors hover:bg-Gray-300"
          >
            <SvgChevrondown
              className={cn(
                'size-16 text-Gray-700 transition-transform duration-200 ease-out',
                isExpanded && 'rotate-180',
              )}
            />
          </button>
        </div>
      ) : null}
    </div>
  );
};
